// @flow

/* eslint react/forbid-prop-types:0 */

import * as React from 'react';
import HiddenDescriptions from 'app/common/react/components/growth/unauth/pin/PinDescription/HiddenDescriptions';
import PinDescriptionContainer from 'app/common/react/components/growth/unauth/pin/PinDescription/PinDescriptionContainer';
import PinDescriptionSeeMore from 'app/common/react/components/growth/unauth/pin/PinDescription/PinDescriptionSeeMore';
import PinnerBoardAttribution from 'app/common/react/components/growth/unauth/pin/PinDescription/PinnerBoardAttribution';
import PropTypes from 'prop-types';
import type ExperimentsClient from 'app/common/lib/ExperimentsClient';
import VaseCarousel from 'app/common/react/components/growth/unauth/pin/PinDescription/VaseCarousel';
import type { ContextType, PinType } from 'app/common/react/components/growth/unauth/lib/PropTypes';
import withUnauthSeoExperiments, {
  ExperimentsPropType,
} from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  shouldUseVaseCarousel,
  getDescriptionAndAnnotations,
  shouldRenderSeeMore,
} from 'app/common/react/components/growth/unauth/pin/pinUtils';

type Props = {
  carouselTagColor: ?string,
  descriptionAsTitle: ?boolean,
  disableDeeplinks: boolean,
  hideVaseTags: ?boolean,
  openInCurrentTab: boolean,
  pin: PinType,
  secondaryFeed?: boolean,
  seoUnauthExperiments: ExperimentsClient,
  showSeeMore: boolean,
  vaseClickHandler: (pin: PinType) => void,
  inPinGridOptGroup?: boolean,
  pinGridOptGroup?: string,
  isPaidTraffic: boolean,
  vaseCarouselStyleOverrides?: {
    gradientLeft?: {
      background: string,
    },
    gradientRight?: {
      background: string,
    },
  },
  richPinAttribution: React.Node,
  richRecipeAttribution: React.Node,
  showPinDescriptionPinPageGroup?: string,
  handleClickSeeMore?: (expandedVASE: boolean) => {},
};

export const PinDescription = (props: Props, context: ContextType) => {
  // Note: any logic that would changes the height of any part of the pin that is visible on pageload
  // should be added to methods in pinUtils.js so that it can be used to measure height of pin on server
  const {
    pin,
    seoUnauthExperiments,
    isPaidTraffic,
    secondaryFeed,
    richPinAttribution,
    richRecipeAttribution,
    inPinGridOptGroup = false,
    hideVaseTags = false,
    pinGridOptGroup = '',
  } = props;

  const { pageType } = context;

  const {
    description,
    vaseAnnotations,
    vaseDescriptions,
    annotationsWithLinks,
  } = getDescriptionAndAnnotations(pin, seoUnauthExperiments, pageType);

  const vaseDescriptionsMarkup = !hideVaseTags && (
    <HiddenDescriptions
      annotations={vaseAnnotations || []}
      description={description || ''}
      vaseTexts={vaseDescriptions || []}
      pin={pin}
      expanded={pin.expandedVASE}
      secondaryFeed={secondaryFeed}
      seoUnauthExperiments={seoUnauthExperiments}
      pinGridOptGroup={pinGridOptGroup}
    />
  );

  const showVaseCarousel = !props.hideVaseTags &&
    shouldUseVaseCarousel(vaseAnnotations, seoUnauthExperiments, isPaidTraffic) && (
      <VaseCarousel
        inRemoveInlineGroup={inPinGridOptGroup}
        openInCurrentTab={props.openInCurrentTab}
        carouselTagColor={props.carouselTagColor}
        styleOverrides={props.vaseCarouselStyleOverrides}
        annotationsWithLinks={annotationsWithLinks}
        disableDeeplinks={props.disableDeeplinks}
        image_signature={pin.image_signature}
      />
    );

  const pinDescriptionInnerHtml = (
    <React.Fragment>
      {inPinGridOptGroup ? null : showVaseCarousel}
      {!isPaidTraffic && !hideVaseTags ? (
        <PinDescriptionContainer
          seoUnauthExperiments={seoUnauthExperiments}
          description={description}
          pin={pin}
          richPinAttribution={richPinAttribution}
          richRecipeAttribution={richRecipeAttribution}
          inPinGridOptGroup={inPinGridOptGroup}
          showVaseCarousel={showVaseCarousel}
        />
      ) : null}
      {pin.pinner &&
        pin.board &&
        pin.board.url && (
          <PinnerBoardAttribution
            pinner={pin.pinner}
            board={pin.board}
            inRemoveInlineGroup={inPinGridOptGroup}
          />
        )}
      {!pinGridOptGroup.startsWith('enabled_clean') && vaseDescriptionsMarkup}
      {!hideVaseTags &&
        shouldRenderSeeMore(
          vaseDescriptions,
          description,
          vaseAnnotations,
          pin,
          seoUnauthExperiments,
          props.showSeeMore,
          secondaryFeed,
        ) && (
          <PinDescriptionSeeMore
            vaseDescriptionsMarkup={vaseDescriptionsMarkup}
            pin={pin}
            handleClickSeeMore={props.handleClickSeeMore}
            vaseClickHandler={props.vaseClickHandler}
            pinGridOptGroup={pinGridOptGroup}
          />
        )}
    </React.Fragment>
  );

  return inPinGridOptGroup ? pinDescriptionInnerHtml : <div>{pinDescriptionInnerHtml}</div>;
};

PinDescription.propTypes = {
  carouselTagColor: PropTypes.string,
  descriptionAsTitle: PropTypes.bool,
  disableDeeplinks: PropTypes.bool,
  isPaidTraffic: PropTypes.bool,
  openInCurrentTab: PropTypes.bool,
  pin: PropTypes.shape({
    board: PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
    }),
    id: PropTypes.string,
    description: PropTypes.string.isRequired,
    expandedVASE: PropTypes.bool,
    fullImage: PropTypes.string,
    image_signature: PropTypes.string,
    place_summary: PropTypes.object,
    pin_join: PropTypes.object,
    pinner: PropTypes.shape({
      full_name: PropTypes.string,
    }),
    rich_metadata: PropTypes.object,
    rich_summary: PropTypes.object,
  }),
  secondaryFeed: PropTypes.bool,
  inPinGridOptGroup: PropTypes.bool,
  pinGridOptGroup: PropTypes.string,
  seoUnauthExperiments: ExperimentsPropType,
  showSeeMore: PropTypes.bool,
  hideVaseTags: PropTypes.bool,
  vaseClickHandler: PropTypes.func.isRequired,
  vaseCarouselStyleOverrides: PropTypes.shape({}),
  showPinDescriptionPinPageGroup: PropTypes.string,
  richPinAttribution: PropTypes.node,
  richRecipeAttribution: PropTypes.node,
  handleClickSeeMore: PropTypes.func,
};

PinDescription.contextTypes = {
  isBot: PropTypes.bool.isRequired,
  pageType: PropTypes.string.isRequired,
  userAgent: PropTypes.shape({
    platform: PropTypes.string.isRequired,
    platformVersion: PropTypes.arrayOf(PropTypes.number),
    raw: PropTypes.string.isRequired,
    browserName: PropTypes.string,
  }).isRequired,
};

export default withUnauthSeoExperiments(PinDescription);
